import { DeliveryIconComponent, DineinIcon, ReserveIcon } from "../../../../../../../../Components/IconComponents";
import PackageIcon from "../../../../../../../../Components/IconComponents/packageIcon";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import {
  IDeliveryOrderData,
  IDineInOrderData,
  IOrderParams,
  IPreOrderData,
  IReserveOrderData,
  OrderTypes,
} from "../../../../../../../../Interfaces/componentTypes";
import {
  formatDate,
  formatTime,
  getDineInLocationLabel,
} from "../../../../../../../../Utilits/utilits";

export type OrderCardTypeClass = "delivery" | "preorder" | "reserve" | "dineIn";

const TYPE_ACCENT_COLORS: Record<OrderCardTypeClass, string> = {
  delivery: "#1d6fd8",
  preorder: "#7a3fd1",
  reserve: "#0f8a6a",
  dineIn: "#d97706",
};

export interface OrderCardHighlight {
  label: string;
  value: string;
}

export interface OrderCardMetaField {
  key: string;
  label: string;
  value: string;
}

export interface CompanyOrderCardContent {
  icon: JSX.Element;
  typeLabel: string;
  typeClass: OrderCardTypeClass;
  highlight: OrderCardHighlight | null;
  metaFields: OrderCardMetaField[];
  total: string | null;
  itemsCount: number | null;
  phone: string | null;
  hasNotes: boolean;
  notesPreview: string | null;
}

const useCompanyOrderCards = (currencySymbol: string) => {
  const { t } = useLanguagesHooks();

  const formatTotal = (value?: number | string | null): string | null => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    return `${value} ${currencySymbol}`;
  };

  const buildNotesPreview = (notes?: string): string | null => {
    if (!notes?.trim()) {
      return null;
    }
    const trimmed = notes.trim();
    return trimmed.length > 72 ? `${trimmed.slice(0, 72)}…` : trimmed;
  };

  function getOrderCardContent(
    order_type: OrderTypes,
    order_parameters: IOrderParams,
    data: string,
  ): CompanyOrderCardContent {
    let parsedData: IDeliveryOrderData | IPreOrderData | IReserveOrderData | IDineInOrderData;

    try {
      parsedData = JSON.parse(data);
    } catch {
      parsedData = {} as IDeliveryOrderData;
    }

    const phone = order_parameters.phone_number || null;
    const itemsCount = order_parameters.totalItems ?? null;
    const hasNotes = Boolean(order_parameters.notes?.trim());
    const notesPreview = buildNotesPreview(order_parameters.notes);

    switch (order_type) {
      case OrderTypes.DELIVERY: {
        const deliveryData = parsedData as IDeliveryOrderData;
        const addressParts = [
          order_parameters.address,
          order_parameters.city,
          order_parameters.region,
        ].filter(Boolean);
        const address = addressParts.join(", ");

        const metaFields: OrderCardMetaField[] = [];
        if (deliveryData.delivery_cost != null) {
          metaFields.push({
            key: "delivery",
            label: t("Visitor.My_Order_Card.Delivery_Fee").replace(/\s*:\s*$/, ""),
            value: `${deliveryData.delivery_cost} ${currencySymbol}`,
          });
        }

        return {
          icon: <DeliveryIconComponent width={28} height={28} color={TYPE_ACCENT_COLORS.delivery} />,
          typeLabel: t("Visitor.My_Order_Card.Delivery"),
          typeClass: "delivery",
          highlight: address
            ? {
                label: t("Visitor.My_Order_Card.Address").replace(/\s*:\s*$/, ""),
                value: address,
              }
            : null,
          metaFields,
          total: formatTotal(deliveryData.total_cost),
          itemsCount,
          phone,
          hasNotes,
          notesPreview,
        };
      }

      case OrderTypes.PREORDER: {
        const preOrderData = parsedData as IPreOrderData;
        const scheduled =
          order_parameters.date || order_parameters.time
            ? `${order_parameters.date ? formatDate(order_parameters.date, false) : ""}${
                order_parameters.time ? ` ${formatTime(order_parameters.time)}` : ""
              }`.trim()
            : null;

        return {
          icon: <PackageIcon width={28} height={28} color={TYPE_ACCENT_COLORS.preorder} />,
          typeLabel: t("Visitor.My_Order_Card.Pre_Order"),
          typeClass: "preorder",
          highlight: scheduled
            ? {
                label: t("Visitor.My_Order_Card.Date").replace(/\s*:\s*$/, ""),
                value: scheduled,
              }
            : null,
          metaFields: [],
          total: formatTotal(preOrderData.total_cost),
          itemsCount,
          phone,
          hasNotes,
          notesPreview,
        };
      }

      case OrderTypes.RESERVE: {
        const reserveData = parsedData as IReserveOrderData;
        const scheduled =
          order_parameters.date || order_parameters.time
            ? `${order_parameters.date ? formatDate(order_parameters.date, false) : ""}${
                order_parameters.time ? ` ${formatTime(order_parameters.time)}` : ""
              }`.trim()
            : null;

        const metaFields: OrderCardMetaField[] = [];
        if (order_parameters.persons_count) {
          metaFields.push({
            key: "persons",
            label: t("Visitor.Reserve.Persons"),
            value: String(order_parameters.persons_count),
          });
        }

        return {
          icon: <ReserveIcon width={28} height={28} color={TYPE_ACCENT_COLORS.reserve} />,
          typeLabel: t("Visitor.My_Order_Card.Reserve"),
          typeClass: "reserve",
          highlight: scheduled
            ? {
                label: t("Visitor.My_Order_Card.Date").replace(/\s*:\s*$/, ""),
                value: scheduled,
              }
            : null,
          metaFields,
          total: formatTotal(reserveData.total_cost),
          itemsCount,
          phone,
          hasNotes,
          notesPreview,
        };
      }

      case OrderTypes.DINE_IN: {
        const dineInData = parsedData as IDineInOrderData;
        const locationLabel = getDineInLocationLabel(order_parameters, dineInData, { t });

        return {
          icon: <DineinIcon width={28} height={28} color={TYPE_ACCENT_COLORS.dineIn} />,
          typeLabel: t("Visitor.My_Order_Card.Dine_In"),
          typeClass: "dineIn",
          highlight: locationLabel
            ? {
                label: t("My_Business.Current_Orders.User_Location").replace(/[：:]\s*$/, ""),
                value: locationLabel,
              }
            : null,
          metaFields: [],
          total: formatTotal(dineInData.total_cost),
          itemsCount,
          phone,
          hasNotes,
          notesPreview,
        };
      }

      default:
        return {
          icon: <></>,
          typeLabel: "Unknown Order",
          typeClass: "delivery",
          highlight: null,
          metaFields: [],
          total: null,
          itemsCount: null,
          phone,
          hasNotes,
          notesPreview,
        };
    }
  }

  return {
    getOrderCardContent,
  };
};

export default useCompanyOrderCards;
